import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { adminApi, Card, Badge } from './_shared';
import { Loader2, Sparkles, Mail, Layers3, Github, Cloud, Cpu, Eye, EyeOff, Save, RotateCcw, PlugZap, ExternalLink, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const ICON = {
  anthropic: Sparkles, resend: Mail, autodesk_aps: Layers3,
  github: Github, aws: Cloud, bedrock: Cpu,
};

const SOURCE_LABEL = {
  database: 'Set in Admin Portal',
  environment: 'From server environment',
  unset: 'Not configured',
};

const ServiceCard = ({ svc, canReveal, onSaved }) => {
  const Ic = ICON[svc.id] || Layers3;
  const [drafts, setDrafts] = useState({});
  const [revealed, setRevealed] = useState({});
  const [shown, setShown] = useState({});
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);

  const dirty = Object.values(drafts).some((v) => v !== undefined && v !== '');

  const setDraft = (env, v) => setDrafts((d) => ({ ...d, [env]: v }));

  const save = async () => {
    setBusy('save');
    try {
      const entries = Object.entries(drafts).filter(([, v]) => v && v.trim());
      for (const [env, v] of entries) await adminApi.saveIntegrationKey(env, v.trim());
      toast.success(`${svc.name} updated — the new values are live immediately`);
      setDrafts({}); setShown({}); setRevealed({}); setResult(null);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally { setBusy(''); }
  };

  const reset = async (env) => {
    setBusy('reset');
    try {
      await adminApi.clearIntegrationKey(env);
      toast.success('Reverted to the server value');
      setDrafts((d) => ({ ...d, [env]: '' }));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset');
    } finally { setBusy(''); }
  };

  const reveal = async (env) => {
    try {
      const r = await adminApi.revealIntegrationKey(env);
      setRevealed((s) => ({ ...s, [env]: r.value || '' }));
      setShown((s) => ({ ...s, [env]: true }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reveal this value');
    }
  };

  const test = async () => {
    setBusy('test'); setResult(null);
    try {
      const values = Object.fromEntries(Object.entries(drafts).filter(([, v]) => v && v.trim()));
      const r = await adminApi.testIntegration(svc.id, values);
      setResult(r);
      r.ok ? toast.success(`${svc.name}: connection verified`) : toast.error(`${svc.name}: ${r.error || 'test failed'}`);
    } catch (err) {
      setResult({ ok: false, error: err.response?.data?.detail || 'Test failed' });
    } finally { setBusy(''); }
  };

  return (
    <Card className="p-6" data-testid={`integration-card-${svc.id}`}>
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1E4E8C]/10 flex items-center justify-center shrink-0">
            <Ic className="w-5 h-5 text-[#1E4E8C]" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base text-slate-800 font-medium leading-tight">{svc.name}</h3>
            <div className="text-[11px] text-slate-500">{svc.vendor}</div>
          </div>
        </div>
        <Badge tone={svc.configured ? 'green' : 'amber'} data-testid={`integration-status-${svc.id}`}>
          {svc.configured ? 'Configured' : 'Missing'}
        </Badge>
      </div>

      <p className="text-xs text-slate-500 mt-3 mb-4 leading-relaxed">{svc.description}</p>

      <div className="space-y-4 border-t border-slate-200 pt-4">
        {svc.fields.map((f) => (
          <div key={f.env}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="text-[11px] uppercase tracking-wider text-slate-500" htmlFor={`in-${f.env}`}>
                {f.label}{f.required && <span className="text-[#F26B1F]"> *</span>}
              </label>
              <span className="text-[10px] text-slate-400">{SOURCE_LABEL[f.source]}</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                id={`in-${f.env}`}
                data-testid={`integration-input-${f.env}`}
                type={f.secret && !shown[f.env] ? 'password' : 'text'}
                value={drafts[f.env] ?? (shown[f.env] ? (revealed[f.env] ?? '') : '')}
                onChange={(e) => setDraft(f.env, e.target.value)}
                placeholder={f.configured ? (f.masked || '••••••••') : (f.placeholder || 'Not set')}
                autoComplete="off"
                className="flex-1 min-w-0 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-mono text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1E4E8C]/30"
              />
              {f.secret && (
                <button
                  type="button"
                  title={canReveal ? 'Reveal the stored value' : 'Only a superadmin can reveal stored keys'}
                  disabled={!canReveal || !f.configured}
                  data-testid={`integration-reveal-${f.env}`}
                  onClick={() => (shown[f.env]
                    ? (setShown((s) => ({ ...s, [f.env]: false })),
                       setRevealed((s) => { const n = { ...s }; delete n[f.env]; return n; }))
                    : reveal(f.env))}
                  className="p-2 rounded-md border border-slate-300 text-slate-500 hover:text-slate-800 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {shown[f.env] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              )}
              {f.source === 'database' && (
                <button
                  type="button"
                  title="Remove this override and use the server value"
                  data-testid={`integration-reset-${f.env}`}
                  onClick={() => reset(f.env)}
                  className="p-2 rounded-md border border-slate-300 text-slate-500 hover:text-[#F26B1F] hover:border-[#F26B1F]/50 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
            </div>
            {f.updatedAt && f.source === 'database' && (
              <div className="text-[10px] text-slate-400 mt-1">
                Updated {new Date(f.updatedAt).toLocaleString()}{f.updatedBy ? ` by ${f.updatedBy}` : ''}
              </div>
            )}
          </div>
        ))}
      </div>

      {result && (
        <div
          data-testid={`integration-test-result-${svc.id}`}
          className={`mt-4 flex items-start gap-2 rounded-md px-3 py-2 text-xs ${result.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
        >
          {result.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span className="break-words">{result.ok ? result.detail : `${result.error || 'Failed'}${result.hint ? ` — ${result.hint}` : ''}`}</span>
        </div>
      )}

      <div className="flex items-center gap-2 mt-5 pt-4 border-t border-slate-200">
        <button
          onClick={save}
          disabled={!dirty || busy === 'save'}
          data-testid={`integration-save-${svc.id}`}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#1E4E8C] text-white text-xs font-medium hover:bg-[#163c6e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
        {svc.testable && (
          <button
            onClick={test}
            disabled={busy === 'test'}
            data-testid={`integration-test-${svc.id}`}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 text-slate-700 text-xs font-medium hover:border-[#1E4E8C]/50 hover:text-[#1E4E8C] disabled:opacity-40 transition-colors"
          >
            {busy === 'test' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />} Test connection
          </button>
        )}
        {svc.docs && (
          <a
            href={svc.docs} target="_blank" rel="noreferrer"
            data-testid={`integration-docs-${svc.id}`}
            className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#F26B1F] transition-colors"
          >
            Where to get this <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </Card>
  );
};

export default function AdminIntegrations() {
  const [data, setData] = useState(null);

  const load = async () => {
    try { setData(await adminApi.integrationKeys()); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not load integrations'); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return (
    <AdminLayout title="Integrations" subtitle="Admin · External Services">
      <div className="flex items-center justify-center py-20" data-testid="integrations-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Integrations" subtitle="Admin · External Services">
      <p className="text-sm text-slate-500 mb-6 max-w-3xl">
        Every third-party service Panorix talks to. Keys saved here are encrypted in the platform database,
        take effect immediately without a restart, and override whatever the server was deployed with.
        Test a key before you save it — nothing is stored until you press Save.
        {!data.canReveal && ' Stored keys can only be revealed by a superadmin.'}
      </p>
      <div className="grid md:grid-cols-2 gap-5" data-testid="integrations-grid">
        {(data.services || []).map((svc) => (
          <ServiceCard key={svc.id} svc={svc} canReveal={data.canReveal} onSaved={load} />
        ))}
      </div>
    </AdminLayout>
  );
}
